import { ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { Global } from 'src/app/core/resources/global';
import { StandardMapComponent } from '../standard-map/standard-map.component';
import { FormControl, Validators } from '@angular/forms';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { GeoValidationService } from 'src/app/core/services/geo-validation.service';
import { municipalityPolygonValidator } from 'src/app/core/validators/municipality-polygon.validator';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-modal-selection-map',
  templateUrl: './modal-selection-map.component.html',
  styleUrls: ['./modal-selection-map.component.scss']
})
export class ModalSelectionMapComponent implements OnInit {
  keyMarker: string = 'associateLocationThirdParty'
  mapOptions: OptionsAutocomplete = {
    zoom: 16,
    keyMarkerListener: this.keyMarker,
    clickMapIcon: this.global.pathMarkerDestination,
    initialClickMap: true
  };
  addressSelected: FormControl = new FormControl('', [Validators.required]);
  addressOptions: OptionsAutocomplete = {
    title: 'Dirección'
  }
  showModal: boolean = false;
  loadingPolygon: boolean = false;
  @ViewChild(StandardMapComponent) standardMap: StandardMapComponent;
  constructor(
    public global: Global,
    public spinner: NgxSpinnerService,
    @Inject(MAT_DIALOG_DATA) public dialogParams: {
      initialValue?: {
        address: string,
        location: {
          lat: number,
          lng: number
        }
      },
      clickMap?: boolean,
      municipalityCode?: string
    },
    public dialogRef: MatDialogRef<ModalSelectionMapComponent>,
    public snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    private geoValidation: GeoValidationService
  ) { }

  ngOnInit() {
    if (!this.dialogParams || !this.dialogParams.clickMap) this.mapOptions.initialClickMap = false;
    this.showModal = true;
    this.setSubscriptions();
    this.setupGeoValidator();
  }

  // Validador async sobre la dirección elegida — solo activo cuando la modal
  // está en modo edición (clickMap) y el caller pasó municipio. Sin municipio
  // la modal mantiene la conducta antigua (no valida, no bloquea Guardar).
  private setupGeoValidator() {
    if (!this.dialogParams || !this.dialogParams.clickMap || !this.dialogParams.municipalityCode) return;
    this.addressSelected.setAsyncValidators(
      municipalityPolygonValidator(this.geoValidation, () => {
        const value = this.addressSelected.value;
        const loc = value && value.location;
        if (!loc || loc.lat == null || loc.lng == null) return null;
        if (loc.lat === 0 && loc.lng === 0) return null;
        return { cityId: this.dialogParams.municipalityCode, lat: +loc.lat, lng: +loc.lng };
      })
    );
    this.addressSelected.updateValueAndValidity();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.cdr.detach();
  }

  onMapReady() {
    if (this.dialogParams && this.dialogParams.initialValue && this.dialogParams.initialValue.address) {
      this.addressSelected.setValue(this.dialogParams.initialValue);
      this.addressOptions = {
        ...this.addressOptions,
        initialValue: this.dialogParams.initialValue.address
      }
    }
    if (this.dialogParams && this.dialogParams.municipalityCode) {
      this.loadingPolygon = true;
      this.geoValidation.getMunicipalityPolygon(this.dialogParams.municipalityCode).subscribe(
        poly => {
          this.loadingPolygon = false;
          if (poly && poly.geometry && this.standardMap) {
            this.standardMap.renderMunicipalityPolygon(poly.geometry, { color: '#2563eb', fillOpacity: 0.1, fit: false });
          }
        },
        () => { this.loadingPolygon = false; }
      );
    }
  }

  async onClickMap($event) {
    const addressName = await this.transformCoordinatesToAddress($event.latLng.lat(), $event.latLng.lng());
    if (!addressName) return this.snackBarService.openSnackBar('No se pudo obtener la dirección', null, 'alert');
    this.addressSelected.setValue({
      address: addressName,
      location: {
        lat: $event.latLng.lat(),
        lng: $event.latLng.lng()
      }
    });
    this.addressOptions = {
      ...this.addressOptions,
      initialValue: addressName
    };
  }

  async transformCoordinatesToAddress(lat: number, lng: number): Promise<string> {
    let geocoderService = new google.maps.Geocoder;
    let latlng = { lat: lat, lng: lng };
    let address = '';
    this.spinner.show();
    await geocoderService.geocode({ 'location': latlng }, (results, status) => {
      if (results.length) {
        this.spinner.hide();
        address = results[0].formatted_address ? results[0].formatted_address : '';
      } else this.spinner.hide();
    });
    return address;
  }

  private setSubscriptions() {
    this.addressSelected.valueChanges.subscribe(adrr => {
      if (adrr && adrr.location && adrr.location.lat && adrr.location.lng) {
        this.standardMap.removeMarker(this.keyMarker);
        this.standardMap.createMarker(this.keyMarker, adrr.location.lat, adrr.location.lng, null, this.global.pathMarkerDestination);
        this.standardMap.setCenterMap(adrr.location.lat, adrr.location.lng, 16);
      } else this.standardMap.removeMarker(this.keyMarker);
    })
  }

  sendDataMapa() {
    // Esperar a que termine la validación geográfica (HTTP al backend con polígono).
    if (this.addressSelected.pending) {
      this.addressSelected.statusChanges.pipe(
        filter(status => status !== 'PENDING'),
        take(1)
      ).subscribe(() => this.sendDataMapa());
      return;
    }
    const errors = this.addressSelected.errors;
    if (errors && errors.outsideMunicipality) {
      const err: any = errors.outsideMunicipality;
      const where = err.municipalityName
        ? `${err.municipalityName}${err.departmentName ? ' (' + err.departmentName + ')' : ''}`
        : 'el municipio seleccionado';
      return this.snackBarService.openSnackBar(
        `La dirección está fuera de ${where}. Mueve el pin al área azul del mapa.`,
        null,
        'alert'
      );
    }
    if (this.addressSelected.invalid) {
      return this.snackBarService.openSnackBar('Debes seleccionar una ubicación en el mapa', null, 'alert');
    }
    this.dialogRef.close({
      state: true,
      data: this.addressSelected.value
    });
  }
}
